import { base64ToBytes } from '@casa/util';

export interface SdpMedia {
    /** `video`, `audio`, ... */
    type: string;
    protocol: string;
    payloadTypes: number[];
    /** `a=` lines of this media section; a flag attribute has an empty value. */
    attributes: [name: string, value: string][];
}

export interface SessionDescription {
    /** Session-level `a=` lines. */
    attributes: [name: string, value: string][];
    media: SdpMedia[];
}

export interface H264Track {
    /** `a=control` of the track, unresolved. */
    control: string | undefined;
    payloadType: number;
    clockRate: number;
    packetizationMode: number;
    /** Six hex digits: profile, constraint flags, level. */
    profileLevelId: string | undefined;
    /** From `sprop-parameter-sets`; a camera may also (or only) send them in-band. */
    sps: Uint8Array | undefined;
    pps: Uint8Array | undefined;
}

function attribute(line: string) : [string, string] {
    const separator = line.indexOf(':');

    return separator < 0 ? [line, ''] : [line.slice(0, separator), line.slice(separator + 1)];
}

export function parseSdp(text: string) : SessionDescription {
    const description: SessionDescription = { attributes: [], media: [] };
    let attributes = description.attributes;
    for (const raw of text.split(/\r\n|\n|\r/)) {
        const line = raw.trim();
        if (line.length < 2 || line[1] !== '=') {
            continue;
        }

        const value = line.slice(2);
        if (line[0] === 'm') {
            const [type = '', , protocol = '', ...formats] = value.split(/\s+/);
            const media: SdpMedia = { type, protocol, payloadTypes: formats.map(Number), attributes: [] };
            description.media.push(media);
            attributes = media.attributes;
        } else if (line[0] === 'a') {
            attributes.push(attribute(value));
        }
    }
    return description;
}

export function sdpAttribute(
    attributes: readonly [string, string][],
    name: string,
    prefix = '',
) : string | undefined {
    return attributes.find(([n, v]) => n === name && v.startsWith(prefix))?.[1];
}

function formatParameters(fmtp: string | undefined) : Map<string, string> {
    const parameters = new Map<string, string>();
    // "97 profile-level-id=4d401f; packetization-mode=1; ..."
    for (const part of (fmtp ?? '').replace(/^\d+\s*/, '').split(';')) {
        const separator = part.indexOf('=');
        if (separator > 0) {
            parameters.set(part.slice(0, separator).trim().toLowerCase(), part.slice(separator + 1).trim());
        }
    }
    return parameters;
}

/** The first H.264 video track of a description, or null when there is none. */
export function findH264Track(description: SessionDescription) : H264Track | null {
    for (const media of description.media) {
        if (media.type !== 'video') {
            continue;
        }

        for (const payloadType of media.payloadTypes) {
            // "97 H264/90000"
            const rtpmap = sdpAttribute(media.attributes, 'rtpmap', `${payloadType} `);
            const match = /^\d+\s+([^/\s]+)\/(\d+)/.exec(rtpmap ?? '');
            if (!match || match[1]!.toUpperCase() !== 'H264') {
                continue;
            }

            const parameters = formatParameters(sdpAttribute(media.attributes, 'fmtp', `${payloadType} `));
            let sps: Uint8Array | undefined;
            let pps: Uint8Array | undefined;
            for (const encoded of (parameters.get('sprop-parameter-sets') ?? '').split(',')) {
                let nal: Uint8Array;
                try {
                    nal = base64ToBytes(encoded.trim());
                } catch {
                    continue;
                }
                if (nal.length === 0) {
                    continue;
                }

                const type = nal[0]! & 0x1f;
                if (type === 7) {
                    sps ??= nal;
                } else if (type === 8) {
                    pps ??= nal;
                }
            }
            return {
                control: sdpAttribute(media.attributes, 'control'),
                payloadType,
                clockRate: Number(match[2]),
                packetizationMode: Number(parameters.get('packetization-mode') ?? '0'),
                profileLevelId: parameters.get('profile-level-id')?.toLowerCase(),
                sps,
                pps,
            };
        }
    }
    return null;
}

/** Codec names of the video tracks, for an error message when none of them is supported. */
export function videoCodecs(description: SessionDescription) : string[] {
    return description.media
        .filter((media) => media.type === 'video')
        .flatMap((media) => media.attributes.filter(([name]) => name === 'rtpmap'))
        .map(([, value]) => /^\d+\s+([^/\s]+)/.exec(value)?.[1] ?? value);
}
